import { Checkbox, FormControlLabel } from "@mui/material";
import { CAB_TYPE_OPTIONS } from "../../constants/vehicleTypes";
import { FUEL_TYPE_OPTIONS } from "../../constants/fuelTypes";

interface FilterOption {
  id: string;
  label: string;
}

interface FilterGroupProps<T extends string> {
  title: string;
  options: FilterOption[];
  filterType: T;
  onChange: (filterType: T, value: string, isChecked: boolean) => void;
  activeFilters: string[];
}

interface SidebarProps {
  onFilterChange: <T extends string>(
    filterType: T,
    value: string,
    isChecked: boolean
  ) => void;
  initialFilters: {
    cabType: string[];
    fuelType: string[];
    cabModel: string[];
  };
}

const FilterGroup = <T extends string>({
  title,
  options,
  filterType,
  onChange,
  activeFilters,
}: FilterGroupProps<T>) => {
  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold mb-2">{title}</h2>
      <div className="flex flex-col">
        {options.map((option) => (
          <FormControlLabel
            key={option.id}
            control={
              <Checkbox
                size="small"
                checked={activeFilters.includes(option.id)}
                onChange={(e) =>
                  onChange(filterType, option.id, e.target.checked)
                }
              />
            }
            label={option.label}
          />
        ))}
      </div>
    </div>
  );
};

// Vehicle/fuel category options sourced from the shared constants, so this
// filter list can never drift from the values the booking API accepts.
const cabTypes: FilterOption[] = CAB_TYPE_OPTIONS.map(({ value, label }) => ({
  id: value,
  label,
}));

const fuelTypes: FilterOption[] = FUEL_TYPE_OPTIONS.map(({ value, label }) => ({
  id: value,
  label,
}));

const cabModels: FilterOption[] = [
  { id: "wagonr", label: "Wagon R" },
  { id: "swift", label: "Swift Dezire" },
  { id: "ertiga", label: "Maruti Suzuki Ertiga" },
  { id: "innova", label: "Toyota Innova" },
];

const Sidebar = ({ onFilterChange, initialFilters }: SidebarProps) => {
  return (
    <div className="bg-white p-6 h-screen overflow-y-auto w-full">
      <FilterGroup
        title="Cab Type"
        options={cabTypes}
        filterType="cabType"
        onChange={onFilterChange}
        activeFilters={initialFilters.cabType}
      />
      <FilterGroup
        title="Fuel Type"
        options={fuelTypes}
        filterType="fuelType"
        onChange={onFilterChange}
        activeFilters={initialFilters.fuelType}
      />
      <FilterGroup
        title="Cab Model"
        options={cabModels}
        filterType="cabModel"
        onChange={onFilterChange}
        activeFilters={initialFilters.cabModel}
      />
    </div>
  );
};

export default Sidebar;
